import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import MovieCard from "@/components/base/MovieCard";
import Pagination from "@/components/base/Pagination";
import FilterPanel, { type Filters } from "@/pages/category/components/FilterPanel";
import { movies, yearRanges, getPopularity } from "@/mocks/movies";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 8;

type SortKey = "year" | "popularity" | "rating";

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "popularity", label: "最热门" },
  { key: "year", label: "最新年份" },
  { key: "rating", label: "评分最高" },
];

export default function CategoryPage() {
  const [searchParams] = useSearchParams();
  const initialGenre = searchParams.get("genre");

  const [filters, setFilters] = useState<Filters>({
    genres: initialGenre ? [initialGenre] : [],
    yearRange: "all",
    minRating: 0,
    region: "all",
  });
  const [sortKey, setSortKey] = useState<SortKey>("popularity");
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const pageTitle =
    filters.genres.length === 1
      ? `${filters.genres[0]}片 - 经典80年代电影资源 | 80s电影`
      : "电影分类 - 经典80年代电影资源 | 80s电影";

  usePageMeta(
    pageTitle,
    filters.genres.length === 1
      ? `精选80年代${filters.genres[0]}类型经典电影，提供剧情介绍、演员信息与影评解读。`
      : "按类型、年份、评分与地区浏览80年代经典电影，发现属于你的那部老片。",
    "80s电影,电影分类,经典电影,怀旧电影"
  );

  const filtered = useMemo(() => {
    let list = movies.filter((movie) => {
      if (filters.genres.length > 0) {
        const matchGenre = filters.genres.some((g) => movie.genres.includes(g));
        if (!matchGenre) return false;
      }
      if (filters.yearRange !== "all") {
        const range = yearRanges.find((r) => r.label === filters.yearRange);
        if (range && (movie.year < range.min || movie.year > range.max)) {
          return false;
        }
      }
      if (filters.minRating > 0 && movie.rating < filters.minRating) {
        return false;
      }
      if (filters.region !== "all" && movie.region !== filters.region) {
        return false;
      }
      return true;
    });

    list = [...list].sort((a, b) => {
      if (sortKey === "year") return b.year - a.year;
      if (sortKey === "rating") return b.rating - a.rating;
      return getPopularity(b.id) - getPopularity(a.id);
    });

    return list;
  }, [filters, sortKey]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const updateFilters = (next: Partial<Filters>) => {
    setFilters((prev) => ({ ...prev, ...next }));
    setPage(1);
  };

  const toggleGenre = (genre: string) => {
    setFilters((prev) => ({
      ...prev,
      genres: prev.genres.includes(genre)
        ? prev.genres.filter((g) => g !== genre)
        : [...prev.genres, genre],
    }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({ genres: [], yearRange: "all", minRating: 0, region: "all" });
    setPage(1);
  };

  const sectionTitle =
    filters.genres.length === 1 ? `${filters.genres[0]}片` : "全部电影分类";

  return (
    <PageLayout>
      <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6">
        <nav className="mb-2 flex items-center gap-1.5 font-label text-xs text-foreground-500">
          <Link to="/" className="hover:text-accent-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">电影分类</span>
        </nav>

        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {sectionTitle}
        </h1>
        <p className="mt-2 max-w-2xl font-label text-sm text-foreground-600">
          {filters.genres.length === 1
            ? `精选80年代${filters.genres[0]}类型经典影片，涵盖剧情简介、演员信息与影评。`
            : "按类型、年份、评分与地区多维筛选，发现属于你的那部80年代经典电影。"}
        </p>

        <button
          type="button"
          onClick={() => setMobileFilterOpen((v) => !v)}
          className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 font-label text-sm text-foreground-700 lg:hidden"
        >
          <i className="ri-filter-3-line"></i>
          筛选条件
          <i className={mobileFilterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
        </button>

        <div className="mt-6 flex flex-col gap-6 lg:flex-row">
          <div className={`${mobileFilterOpen ? "block" : "hidden"} w-full lg:block lg:w-72 lg:shrink-0`}>
            <FilterPanel
              filters={filters}
              resultCount={filtered.length}
              onToggleGenre={toggleGenre}
              onSetYear={(label) => updateFilters({ yearRange: label })}
              onSetRating={(value) => updateFilters({ minRating: value })}
              onSetRegion={(region) => updateFilters({ region })}
              onReset={resetFilters}
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <p className="font-label text-sm text-foreground-600">
                共 {filtered.length} 部影片
              </p>
              <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setSortKey(opt.key)}
                    className={`whitespace-nowrap rounded-full px-3 py-1.5 font-label text-xs transition-colors ${
                      sortKey === opt.key
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {pageItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
                <i className="ri-film-line mb-3 flex h-14 w-14 items-center justify-center text-3xl text-foreground-300"></i>
                <p className="font-label text-sm text-foreground-600">
                  暂无符合条件的电影
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-refresh-line"></i>
                  重置筛选条件
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                  {pageItems.map((movie, index) => (
                    <MovieCard key={movie.id} movie={movie} index={index} />
                  ))}
                </div>
                <div className="mt-10">
                  <Pagination
                    page={currentPage}
                    totalPages={totalPages}
                    onChange={setPage}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
}